import { useCallback, useRef, useState } from "react";
import { getWorkspaceDetailApi, renameWorkspaceApi } from "../../../api/workspaceApi";
import { errorDetail } from "../../../helper/errorDetail";
import { mapWorkspaceToSavedSearchOption } from "../../Workspaces/savedSearchQuery";

function useSavedSearchRename({
    onEditSearchSuccess, savedSearchCatalog, setSavedSearchChoices, setActiveFilter,
    setSelectedSavedSearch
}) {
const [isEditingSavedSearch, setIsEditingSavedSearch] = useState(false);

const [editingSavedSearchTitle, setEditingSavedSearchTitle] = useState("");

const [editSnapshot, setEditSnapshot] = useState(null);

const [isSavingEdit, setIsSavingEdit] = useState(false);

const [editSearchError, setEditSearchError] = useState(null);

const onEditSearchSuccessRef = useRef(onEditSearchSuccess);

const resolveRenameOption = useCallback(
    async (selectedSearchObject) => {
      if (!selectedSearchObject) return null;
      if (selectedSearchObject.query && typeof selectedSearchObject.query === "object") {
        return selectedSearchObject;
      }
      if (savedSearchCatalog !== "workspace") {
        return selectedSearchObject;
      }

      const workspaceId = selectedSearchObject.id || selectedSearchObject._id;
      if (!workspaceId) return selectedSearchObject;

      try {
        const res = await getWorkspaceDetailApi(workspaceId, 1, 1);
        if (res?.status === 200 && res.data) {
          const mapped = mapWorkspaceToSavedSearchOption(res.data);
          if (mapped) {
            setSavedSearchChoices((prev) =>
              prev.map((row) => ((row.id || row._id) === mapped.id ? { ...row, ...mapped } : row))
            );
            return mapped;
          }
        }
      } catch (error) {
        console.error("Failed to load saved search detail:", error);
      }
      return selectedSearchObject;
    },
    [savedSearchCatalog, setSavedSearchChoices]
  );

const initiateEditSearch = useCallback(
    async (event, option) => {
      event?.stopPropagation?.();
      if (!option) return;
      const resolved = await resolveRenameOption(option);
      if (!resolved) return;
      const title = resolved?.name ? String(resolved.name) : "";
      setEditSnapshot({
        selected: resolved,
        title,
        query: resolved?.query || null,
      });
      setEditingSavedSearchTitle(title);
      setEditSearchError(null);
      setActiveFilter(null);
      setIsEditingSavedSearch(true);
    },
    [resolveRenameOption, setActiveFilter]
  );

const cancelEditSearch = useCallback(() => {
    setEditingSavedSearchTitle("");
    setEditSnapshot(null);
    setEditSearchError(null);
    setActiveFilter(null);
    setIsEditingSavedSearch(false);
  }, [setActiveFilter]);

const confirmEditSearch = useCallback(async () => {
    const target = editSnapshot?.selected;
    const id = target?.id || target?._id;
    const name = editingSavedSearchTitle.trim();
    if (!id || !name) {
      setEditSearchError("Enter a saved search title");
      return;
    }

    setIsSavingEdit(true);
    setEditSearchError(null);
    try {
      const res = await renameWorkspaceApi(id, { name });
      if (!(res && res.status === 200)) {
        throw new Error(errorDetail(res?.data?.detail, "Failed to rename saved search"));
      }

      const responseOption =
        typeof res.data === "object" && res.data
          ? mapWorkspaceToSavedSearchOption(res.data) || {}
          : {};
      const serverQuery = Object.prototype.hasOwnProperty.call(
        res.data || {},
        "source_query"
      )
        ? res.data.source_query
        : target?.query || null;
      const nextOption = {
        ...(target || {}),
        ...responseOption,
        id,
        _id: target?._id || id,
        name,
        query: serverQuery,
      };

      setSavedSearchChoices((prev) =>
        prev.map((row) =>
          (row.id || row._id) === id ? { ...row, ...nextOption } : row
        )
      );
      setSelectedSavedSearch((current) =>
        current && (current.id || current._id) === id
          ? { ...current, ...nextOption }
          : current
      );
      setEditingSavedSearchTitle("");
      setEditSnapshot(null);
      setActiveFilter(null);
      setIsEditingSavedSearch(false);
      onEditSearchSuccessRef.current?.(nextOption, res.data);
    } catch (error) {
      setEditSearchError(error?.message || "Failed to update saved search");
    } finally {
      setIsSavingEdit(false);
    }
  }, [editSnapshot?.selected, editingSavedSearchTitle, setActiveFilter, setSavedSearchChoices, setSelectedSavedSearch]);

return {
    isEditingSavedSearch, editingSavedSearchTitle, setEditingSavedSearchTitle, editSnapshot,
    isSavingEdit, editSearchError, onEditSearchSuccessRef, initiateEditSearch,
    cancelEditSearch, confirmEditSearch
};
}

export { useSavedSearchRename };
